/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** Which four things the summary states back, and whose plan they come from. */

import type { PlanWithFrequency } from '../../hooks/use-plans';
import {
  enabledFeatures, featureMatrix, formatStorage,
  type QuotaLabels, type ResolvedFeatureRow,
} from './PlanChoice.catalog';

/** How many the summary shows. More turns the column into a second plan card. */
export const HIGHLIGHT_COUNT = 4;

/**
 * The baseline every plan carries, which separates nothing.
 *
 * CRM is the obvious one. Social media publishing joins it because it is on
 * for all three tiers at the same flat "Incluído", and four lines are few
 * enough that one spent on a thing the buyer gets either way is one not spent
 * on the AI allowance, the storage or the site -- the three that actually
 * differ between what they are choosing between.
 *
 * `agency_site` is flat-included too and is deliberately kept: it is the only
 * one of the four that is a whole product rather than a quota, and a summary
 * that omitted it would read as if the website were extra.
 */
const EXCLUDED = new Set(['crm', 'social_media']);

/**
 * Whose features the summary describes.
 *
 * A team that mixes tiers has no single plan to speak for it, and the highest
 * one is the honest answer: it is the ceiling the account runs under, and the
 * storage pool is already sized by it.
 *
 * @param plans - The plans on offer, lowest tier first.
 * @param seatsBy - Seats chosen per plan key.
 * @param fallbackKey - The plan to describe when nobody has a seat yet.
 * @returns The plan to read the highlights from, when there is one.
 */
export function highlightPlan(
  plans: PlanWithFrequency[],
  seatsBy: Record<string, number>,
  fallbackKey?: string,
): PlanWithFrequency | undefined {
  const taken = plans.filter((plan) => (seatsBy[plan.key] ?? 0) > 0);
  if (taken.length) return taken[taken.length - 1];
  return plans.find((plan) => plan.key === fallbackKey) ?? plans[0];
}

/**
 * The four rows the summary prints.
 *
 * Only rows the plan has: a summary is not a comparison, and a cross beside
 * something the buyer is about to pay for reads as a warning.
 *
 * @param plan - The plan to describe.
 * @param quotaLabels - The quota ladder, keyed by plan.
 * @param language - The active language, for the storage figure.
 * @returns At most four resolved rows.
 */
export function highlightRows(
  plan: PlanWithFrequency | undefined,
  quotaLabels: QuotaLabels,
  language: string,
): ResolvedFeatureRow[] {
  if (!plan) return [];
  const rows = featureMatrix(
    {
      features: enabledFeatures(plan.features),
      limits: plan.limits,
      quotas: quotaLabels[plan.key] ?? {},
    },
    { storage: (gb) => formatStorage(gb, (n) => n.toLocaleString(language)) },
  );
  return rows
    .filter((row) => row.on && !EXCLUDED.has(row.id))
    .slice(0, HIGHLIGHT_COUNT);
}
